import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowUp,
  FaBell,
  FaChartBar,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaCog,
  FaFileExport,
  FaHeartbeat,
  FaLightbulb,
  FaMagic,
  FaUsers,
} from 'react-icons/fa';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { httpAdmin } from '../lib/http';
import { useGuard401 } from './adminShared';
import './AdminAnalytics.css';

const TABS = [
  { id: 'overview', label: 'Overview', blurb: 'Platform KPIs, trends, funnel, and event volume.' },
  { id: 'behavior', label: 'Behavior', blurb: 'Engagement, popular pages, and device mix.' },
  { id: 'business', label: 'Business', blurb: 'Partner bookings, category/location mixes, and reviews.' },
  { id: 'realtime', label: 'Realtime', blurb: 'Live users, bookings, events, and activity feed.' },
  { id: 'reports', label: 'Reports', blurb: 'API-backed KPI, trends, forecasts, and CSV export.' },
];

const RANGES = [
  { id: '7d', label: '7 Days' },
  { id: '30d', label: '30 Days' },
  { id: '90d', label: '90 Days' },
  { id: '1y', label: '1 Year' },
  { id: 'custom', label: 'Custom' },
];

const FUNNEL_COLORS = ['#18442A', '#2d6a4f', '#40916c', '#52b788', '#ffaa06'];
const EVENT_COLORS = ['#18442A', '#2d6a4f', '#40916c', '#52b788', '#95d5b2', '#ffaa06', '#b08968'];

const EMPTY_PLATFORM = {
  overview: {
    totalUsers: 0,
    totalBusinesses: 0,
    totalBookings: 0,
    totalRevenue: 0,
    activeUsers: 0,
    newUsers: 0,
    growthRates: { bookings: 0, revenue: 0, users: 0 },
  },
  charts: {
    bookingsPerDay: [],
    revenuePerDay: [],
    eventAnalytics: [],
    deviceAnalytics: [],
    popularPages: [],
    searchAnalytics: [],
    conversionFunnel: [],
    compareBookingsPerDay: [],
    compareRevenuePerDay: [],
  },
};

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-EG', {
    style: 'currency',
    currency: 'EGP',
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num || 0);
}

function downloadCsv(filename, rows) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}

function mergeCompareSeries(current, compare, valueKey) {
  const curr = Array.isArray(current) ? current : [];
  const prev = Array.isArray(compare) ? compare : [];
  const len = Math.max(curr.length, prev.length);
  const out = [];
  for (let i = 0; i < len; i += 1) {
    const c = curr[i];
    const p = prev[i];
    out.push({
      _id: c?._id || `period-${i + 1}`,
      [valueKey]: c?.[valueKey] ?? 0,
      compare: p?.[valueKey] ?? null,
      compareLabel: p?._id || null,
    });
  }
  return out;
}

function flattenEventSeries(eventAnalytics) {
  const rows = Array.isArray(eventAnalytics) ? eventAnalytics : [];
  const typeSet = new Set();
  rows.forEach((day) => {
    (day.events || []).forEach((e) => {
      if (e.eventType) typeSet.add(e.eventType);
    });
  });
  const types = [...typeSet].slice(0, 6);
  const series = rows.map((day) => {
    const point = { _id: day._id, totalEvents: day.totalEvents || 0 };
    types.forEach((t) => {
      point[t] = (day.events || [])
        .filter((e) => e.eventType === t)
        .reduce((sum, e) => sum + (e.count || 0), 0);
    });
    return point;
  });
  return { series, types };
}

function ChartEmpty({ title, hint }) {
  return (
    <div className="adm-an-empty" role="status">
      <div className="adm-an-empty__mark" aria-hidden="true">📊</div>
      <p className="adm-an-empty__title">{title}</p>
      <p className="adm-an-empty__hint">{hint}</p>
    </div>
  );
}

function SkeletonGrid({ count = 6 }) {
  return (
    <div className="adm-an-grid" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="adm-an-skel-card">
          <div className="adm-an-skel adm-an-skel--sm" />
          <div className="adm-an-skel adm-an-skel--lg" />
          <div className="adm-an-skel adm-an-skel--md" />
        </div>
      ))}
    </div>
  );
}

function MetricCard({ label, value, hint, change, tone = 'default' }) {
  const changeNum = Number(change);
  const hasChange = Number.isFinite(changeNum) && change != null;
  return (
    <article className={`adm-an-card adm-an-card--${tone}`}>
      <div className="adm-an-card__label">{label}</div>
      <div className="adm-an-card__value">{value}</div>
      {hasChange && (
        <div className={`adm-an-card__change ${changeNum >= 0 ? 'is-up' : 'is-down'}`}>
          {changeNum >= 0 ? '↗' : '↘'} {Math.abs(changeNum).toFixed(1)}% vs prior
        </div>
      )}
      {hint && <div className="adm-an-card__hint">{hint}</div>}
    </article>
  );
}

function ChartPanel({ title, summary, onExport, children, empty, emptyTitle, emptyHint }) {
  return (
    <section className="adm-an-chart" aria-label={title}>
      <header className="adm-an-chart__head">
        <div>
          <h3 id={`${title}-heading`}>{title}</h3>
          {summary && <p className="adm-an-chart__summary">{summary}</p>}
        </div>
        {onExport && !empty && (
          <button type="button" className="adm-an-export" onClick={onExport}>
            <FaFileExport aria-hidden="true" /> CSV
          </button>
        )}
      </header>
      {empty ? (
        <ChartEmpty title={emptyTitle || 'No data for this range'} hint={emptyHint || 'Try another period or wait for new events.'} />
      ) : (
        children
      )}
    </section>
  );
}

function buildQuery({ range, from, to, compare }) {
  const params = new URLSearchParams();
  if (range === 'custom' && from && to) {
    params.set('from', from);
    params.set('to', to);
    params.set('range', 'custom');
  } else {
    params.set('range', range || '30d');
  }
  if (compare) params.set('compare', '1');
  params.set('groupBy', 'day');
  return params.toString();
}

export default function AdminAnalytics() {
  const guard = useGuard401();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = TABS.some((t) => t.id === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'overview';
  const rangeParam = searchParams.get('range') || '30d';
  const timeRange = RANGES.some((r) => r.id === rangeParam) ? rangeParam : '30d';
  const customFrom = searchParams.get('from') || '';
  const customTo = searchParams.get('to') || '';
  const compare = searchParams.get('compare') === '1';

  const [data, setData] = useState(EMPTY_PLATFORM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realTimeData, setRealTimeData] = useState(null);
  const [realtimeUpdatedAt, setRealtimeUpdatedAt] = useState(null);
  const [behavior, setBehavior] = useState(null);
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [behaviorError, setBehaviorError] = useState('');
  const [behaviorTick, setBehaviorTick] = useState(0);
  const [businessData, setBusinessData] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessError, setBusinessError] = useState('');
  const [businessTick, setBusinessTick] = useState(0);
  const [reportsData, setReportsData] = useState(null);
  const [reportsError, setReportsError] = useState('');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportKind, setReportKind] = useState('kpi');
  const [draftFrom, setDraftFrom] = useState(customFrom);
  const [draftTo, setDraftTo] = useState(customTo);

  const patchParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '' || v === false) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setTab = (tab) => patchParams({ tab });
  const setRange = (range) => {
    if (range === 'custom') {
      patchParams({ range: 'custom', from: customFrom || draftFrom, to: customTo || draftTo });
    } else {
      patchParams({ range, from: null, to: null });
    }
  };

  const applyCustomRange = () => {
    if (!draftFrom || !draftTo) return;
    if (new Date(draftFrom) >= new Date(draftTo)) {
      setError('Custom range requires from < to');
      return;
    }
    patchParams({ range: 'custom', from: draftFrom, to: draftTo, tab: activeTab });
  };

  const querySuffix = useMemo(
    () => buildQuery({
      range: timeRange,
      from: customFrom,
      to: customTo,
      compare,
    }),
    [timeRange, customFrom, customTo, compare],
  );

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [platformRes, realTimeRes] = await Promise.all([
        httpAdmin.get(`/api/admin/analytics/platform?${querySuffix}`),
        httpAdmin.get('/api/admin/analytics/realtime'),
      ]);
      const platformData = platformRes || {};
      setData({
        overview: {
          ...EMPTY_PLATFORM.overview,
          ...(platformData.overview || {}),
          growthRates: {
            ...EMPTY_PLATFORM.overview.growthRates,
            ...(platformData.overview?.growthRates || {}),
          },
        },
        charts: {
          ...EMPTY_PLATFORM.charts,
          ...(platformData.charts || {}),
        },
      });
      setRealTimeData(realTimeRes || null);
      setRealtimeUpdatedAt(new Date());
    } catch (e) {
      if (!guard(e)) setError(e?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [querySuffix, guard]);

  const loadRealtimeOnly = useCallback(async () => {
    try {
      const realTime = await httpAdmin.get('/api/admin/analytics/realtime');
      setRealTimeData(realTime || null);
      setRealtimeUpdatedAt(new Date());
    } catch (e) {
      if (!guard(e)) console.error('Realtime analytics load error', e);
    }
  }, [guard]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    if (activeTab !== 'realtime' && activeTab !== 'overview') return undefined;
    const interval = setInterval(loadRealtimeOnly, 15000);
    return () => clearInterval(interval);
  }, [activeTab, loadRealtimeOnly]);

  useEffect(() => {
    let cancelled = false;
    async function loadBehavior() {
      if (activeTab !== 'behavior') return;
      setBehaviorLoading(true);
      setBehaviorError('');
      try {
        const res = await httpAdmin.get(`/api/admin/analytics/behavior?${querySuffix}`);
        if (!cancelled) setBehavior(res?.data || res || null);
      } catch (e) {
        if (!guard(e) && !cancelled) {
          setBehaviorError(e?.message || 'Failed to load behavior');
          setBehavior(null);
        }
      } finally {
        if (!cancelled) setBehaviorLoading(false);
      }
    }
    loadBehavior();
    return () => { cancelled = true; };
  }, [activeTab, querySuffix, guard, behaviorTick]);

  useEffect(() => {
    let cancelled = false;
    async function loadBusiness() {
      if (activeTab !== 'business') return;
      setBusinessLoading(true);
      setBusinessError('');
      try {
        const res = await httpAdmin.get(`/api/admin/analytics/business?${querySuffix}`);
        if (cancelled) return;
        const payload = res?.data || res || {};
        const metricsRow = Array.isArray(payload.metrics) ? payload.metrics[0] : payload.metrics;
        setBusinessData({
          metrics: metricsRow || null,
          topBusinesses: Array.isArray(payload.topBusinesses) ? payload.topBusinesses : [],
          categoryAnalytics: Array.isArray(payload.categoryAnalytics) ? payload.categoryAnalytics : [],
          locationAnalytics: Array.isArray(payload.locationAnalytics) ? payload.locationAnalytics : [],
          reviewAnalytics: Array.isArray(payload.reviewAnalytics)
            ? payload.reviewAnalytics[0]
            : (payload.reviewAnalytics || null),
        });
      } catch (e) {
        if (!guard(e) && !cancelled) {
          setBusinessError(e?.message || 'Failed to load business analytics');
          setBusinessData(null);
        }
      } finally {
        if (!cancelled) setBusinessLoading(false);
      }
    }
    loadBusiness();
    return () => { cancelled = true; };
  }, [activeTab, querySuffix, guard, businessTick]);

  const liveWindowLabel = useMemo(() => {
    const mins = realTimeData?.live?.windowMinutes || 15;
    return `${mins} min`;
  }, [realTimeData]);

  const bookingsChart = useMemo(
    () => mergeCompareSeries(
      data.charts?.bookingsPerDay,
      compare ? data.charts?.compareBookingsPerDay : null,
      'count',
    ),
    [data.charts, compare],
  );

  const revenueChart = useMemo(
    () => mergeCompareSeries(
      data.charts?.revenuePerDay,
      compare ? data.charts?.compareRevenuePerDay : null,
      'revenue',
    ),
    [data.charts, compare],
  );

  const { series: eventSeries, types: eventTypes } = useMemo(
    () => flattenEventSeries(data.charts?.eventAnalytics),
    [data.charts?.eventAnalytics],
  );

  const funnelChartData = useMemo(
    () => (data.charts?.conversionFunnel || []).map((item) => ({
      stage: item._id || item.stage || 'Unknown',
      count: item.count || 0,
    })),
    [data.charts?.conversionFunnel],
  );

  const searchChartData = useMemo(
    () => (data.charts?.searchAnalytics || []).slice(0, 8).map((item) => ({
      term: item._id || item.term || 'Unknown',
      count: item.count || item.searchCount || 0,
    })),
    [data.charts?.searchAnalytics],
  );

  const pageViews = useMemo(
    () => behavior?.pageViews || data.charts?.popularPages || [],
    [behavior, data.charts?.popularPages],
  );
  const devices = useMemo(
    () => behavior?.deviceBreakdown || data.charts?.deviceAnalytics || [],
    [behavior, data.charts?.deviceAnalytics],
  );
  const engagement = behavior?.engagement || null;

  const pageViewsTotal = useMemo(
    () => pageViews.reduce((sum, p) => sum + ((p.viewCount ?? p.count) || 0), 0),
    [pageViews],
  );
  const deviceSessionsTotal = useMemo(
    () => devices.reduce((sum, d) => sum + ((d.sessionCount ?? d.count) || 0), 0),
    [devices],
  );

  const liveActiveUsers = realTimeData?.activeUsers ?? data.overview?.activeUsers ?? 0;
  const biz = businessData?.metrics;
  const categoryChart = useMemo(
    () => (businessData?.categoryAnalytics || []).slice(0, 8).map((c) => ({
      name: c._id || 'Unknown',
      bookings: c.bookingCount || 0,
      revenue: c.revenue || 0,
    })),
    [businessData],
  );
  const locationChart = useMemo(
    () => (businessData?.locationAnalytics || []).slice(0, 8).map((c) => ({
      name: c._id || 'Unknown',
      bookings: c.bookingCount || 0,
      revenue: c.revenue || 0,
    })),
    [businessData],
  );

  const tabMeta = TABS.find((t) => t.id === activeTab) || TABS[0];

  const loadReport = async (kind) => {
    setReportKind(kind);
    setReportsError('');
    setReportsLoading(true);
    setReportsData(null);
    try {
      if (kind === 'kpi') {
        const res = await httpAdmin.get(`/api/admin/reports/kpi?${querySuffix}`);
        const payload = res?.data || res || {};
        const k = payload.kpis || {};
        setReportsData({
          title: 'KPI Dashboard',
          insights: [
            `Total users: ${k.totalUsers ?? 0}`,
            `Conversion rate: ${Number(k.conversionRate || 0).toFixed(1)}%`,
            `Revenue growth: ${Number(k.revenueGrowthRate || 0).toFixed(1)}%`,
            `Retention: ${Number(k.retentionRate || 0).toFixed(1)}%`,
            `Avg booking value: ${formatCurrency(k.avgBookingValue || 0)}`,
          ],
          recommendations: Array.isArray(payload.recommendations) ? payload.recommendations : [],
        });
      } else if (kind === 'trends') {
        const res = await httpAdmin.get(`/api/admin/reports/trends?${querySuffix}&metric=bookings`);
        const payload = res?.data || res || {};
        const t = payload.trends?.trendAnalysis || {};
        setReportsData({
          title: 'Trend Analysis',
          insights: [
            `Direction: ${t.direction || 'stable'}`,
            `Strength: ${t.strength != null ? Number(t.strength).toFixed(1) : 0}%`,
            `Change: ${t.percentageChange != null ? Number(t.percentageChange).toFixed(1) : 0}%`,
            `Data points: ${payload.trends?.data?.length || 0}`,
          ],
          recommendations: Array.isArray(payload.recommendations) ? payload.recommendations : [],
        });
      } else if (kind === 'predictions') {
        const res = await httpAdmin.get(`/api/admin/reports/predictions?${querySuffix}`);
        const payload = res?.data || res || {};
        const p = payload.predictions || {};
        setReportsData({
          title: 'Predictive Analytics',
          insights: [
            `Next 7 days (day 1): ${p.next7Days?.[0]?.predictedValue ?? 0} bookings`,
            `Next 30 days (day 1): ${p.next30Days?.[0]?.predictedValue ?? 0} bookings`,
            `Peak day index: ${p.seasonalTrends?.peakDay ?? '—'}`,
          ],
          recommendations: Array.isArray(p.recommendations) ? p.recommendations : [],
        });
      }
    } catch (e) {
      if (!guard(e)) setReportsError(e?.message || 'Failed to load report');
      setReportsData(null);
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'reports') loadReport(reportKind || 'kpi');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, querySuffix]);

  const exportReports = async () => {
    try {
      const resp = await httpAdmin.fetch(`/api/admin/reports/export?${querySuffix}&format=csv&reportType=comprehensive`);
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booksy-report-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      if (!guard(e)) setReportsError(e?.message || 'Export failed');
    }
  };

  const drillBusiness = (b) => {
    const q = b?.businessName || '';
    nav(`/admin/providers${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  };

  const drillSearch = (term) => {
    nav(`/admin/activity?search=${encodeURIComponent(term || '')}`);
  };

  return (
    <section className="adm-page adm-page--analytics">
      <header className="adm-page-head adm-an-head">
        <div>
          <h2>Analytics</h2>
          <p className="adm-an-desc">{tabMeta.blurb}</p>
        </div>
      </header>

      <div className="adm-an-tools">
        <div className="adm-an-tools__row adm-an-tools__row--range">
          <div className="adm-an-chip-scroll" role="group" aria-label="Time range">
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`adm-filter-chip ${timeRange === r.id ? 'is-active' : ''}`}
                onClick={() => setRange(r.id)}
                aria-pressed={timeRange === r.id}
              >
                {r.label}
              </button>
            ))}
          </div>
          <label className="adm-an-compare">
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => patchParams({ compare: e.target.checked ? '1' : null })}
            />
            Compare prior period
          </label>
        </div>

        {timeRange === 'custom' && (
          <div className="adm-an-tools__custom">
            <label>
              From
              <input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
            </label>
            <button type="button" className="submit-button" onClick={applyCustomRange}>Apply</button>
          </div>
        )}

        <div className="adm-an-tabs adm-an-chip-scroll" role="group" aria-label="Analytics tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`adm-filter-chip ${activeTab === t.id ? 'is-active' : ''}`}
              onClick={() => setTab(t.id)}
              aria-pressed={activeTab === t.id}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="adm-error" role="alert">
          <span>{error}</span>
          <button type="button" className="btn-sm btn-secondary" onClick={loadAnalytics}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="adm-an-loading" aria-busy="true" aria-label="Loading analytics">
          <SkeletonGrid count={6} />
          <div className="adm-an-skel-chart" />
          <div className="adm-an-skel-chart" />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <>
              <div className="adm-live-strip">
                <div className="adm-live-strip-item">
                  <span className="live-dot" />
                  <strong>{liveActiveUsers}</strong>
                  <span>live users ({liveWindowLabel})</span>
                </div>
                <div className="adm-live-strip-item">
                  <FaClipboardList aria-hidden="true" />
                  <strong>{realTimeData?.recentBookings ?? 0}</strong>
                  <span>bookings ({liveWindowLabel})</span>
                </div>
                <div className="adm-live-strip-item">
                  <FaCog aria-hidden="true" />
                  <strong>{realTimeData?.recentEvents ?? 0}</strong>
                  <span>events ({liveWindowLabel})</span>
                </div>
                <div className="adm-live-strip-item">
                  <FaHeartbeat aria-hidden="true" />
                  <strong>{realTimeData?.systemHealth?.databaseStatus || '—'}</strong>
                  <span>database</span>
                </div>
                {realtimeUpdatedAt && (
                  <div className="adm-live-strip-meta">
                    Updated {realtimeUpdatedAt.toLocaleTimeString()}
                  </div>
                )}
              </div>

              <div className="adm-an-grid">
                <MetricCard
                  label="Total Bookings"
                  value={formatNumber(data.overview.totalBookings)}
                  change={data.overview.growthRates.bookings}
                  hint="Active appointments"
                  tone="primary"
                />
                <MetricCard
                  label="Total Revenue"
                  value={formatCurrency(data.overview.totalRevenue)}
                  change={data.overview.growthRates.revenue}
                  hint="Platform earnings"
                  tone="success"
                />
                <MetricCard
                  label="Total Users"
                  value={formatNumber(data.overview.totalUsers)}
                  change={data.overview.growthRates.users}
                  hint="Registered customers"
                  tone="info"
                />
                <MetricCard
                  label="Total Businesses"
                  value={formatNumber(data.overview.totalBusinesses)}
                  hint="Partner businesses"
                  tone="warning"
                />
                <MetricCard
                  label="Active Users"
                  value={formatNumber(data.overview.activeUsers)}
                  hint="Logged in this period"
                />
                <MetricCard
                  label="Avg. Booking Value"
                  value={data.overview.totalBookings > 0
                    ? formatCurrency(data.overview.totalRevenue / data.overview.totalBookings)
                    : formatCurrency(0)}
                  hint="Per appointment"
                  tone="accent"
                />
              </div>

              <section className="adm-an-section" aria-labelledby="an-insights">
                <header className="adm-an-section__head">
                  <h3 id="an-insights">Key Insights</h3>
                  <p>Derived from the selected range vs the prior equal period.</p>
                </header>
                <div className="adm-an-insights">
                  <article className="adm-an-insight">
                    <FaArrowUp aria-hidden="true" />
                    <div>
                      <h4>Growth Trend</h4>
                      <p>
                        {data.overview.growthRates.bookings > 0
                          ? `Bookings are up ${data.overview.growthRates.bookings.toFixed(1)}% this period`
                          : data.overview.growthRates.bookings < 0
                            ? `Bookings are down ${Math.abs(data.overview.growthRates.bookings).toFixed(1)}% this period`
                            : 'Booking growth is stable this period'}
                      </p>
                    </div>
                  </article>
                  <article className="adm-an-insight">
                    <FaChartLine aria-hidden="true" />
                    <div>
                      <h4>Revenue Performance</h4>
                      <p>
                        {data.overview.growthRates.revenue > 0
                          ? `Revenue increased by ${data.overview.growthRates.revenue.toFixed(1)}%`
                          : data.overview.growthRates.revenue < 0
                            ? `Revenue decreased by ${Math.abs(data.overview.growthRates.revenue).toFixed(1)}%`
                            : 'Revenue remains stable'}
                      </p>
                    </div>
                  </article>
                  <article className="adm-an-insight">
                    <FaUsers aria-hidden="true" />
                    <div>
                      <h4>User Growth</h4>
                      <p>
                        {data.overview.newUsers > 0
                          ? `${data.overview.newUsers} new users joined this period`
                          : 'No new users this period'}
                      </p>
                    </div>
                  </article>
                </div>
              </section>

              <div className="adm-an-charts">
                <ChartPanel
                  title="Bookings Over Time"
                  summary={compare ? 'Current period vs prior period (aligned by day index)' : 'Daily booking volume'}
                  empty={!bookingsChart.some((d) => (d.count || 0) > 0)}
                  emptyTitle="No bookings in this range"
                  emptyHint="New bookings will appear here once they start coming in."
                  onExport={() => downloadCsv('bookings-over-time.csv', bookingsChart.map((d) => ({
                    date: d._id,
                    bookings: d.count,
                    prior: d.compare ?? '',
                  })))}
                >
                  <div className="adm-an-chart__body" role="img" aria-label="Line chart of bookings over time">
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={bookingsChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,68,42,0.12)" />
                        <XAxis dataKey="_id" tick={{ fontSize: 11 }} tickFormatter={(v) => {
                          const d = new Date(v);
                          return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
                        }}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" name="Bookings" stroke="#18442A" strokeWidth={2.5} dot={false} />
                        {compare && (
                          <Line type="monotone" dataKey="compare" name="Prior period" stroke="#95d5b2" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </ChartPanel>

                <ChartPanel
                  title="Revenue Over Time"
                  summary={compare ? 'Completed revenue vs prior period' : 'Completed booking revenue'}
                  empty={!revenueChart.some((d) => (d.revenue || 0) > 0)}
                  emptyTitle="No revenue in this range"
                  emptyHint="Completed bookings with revenue will populate this chart."
                  onExport={() => downloadCsv('revenue-over-time.csv', revenueChart.map((d) => ({
                    date: d._id,
                    revenue: d.revenue,
                    prior: d.compare ?? '',
                  })))}
                >
                  <div className="adm-an-chart__body" role="img" aria-label="Line chart of revenue over time">
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={revenueChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,68,42,0.12)" />
                        <XAxis dataKey="_id" tick={{ fontSize: 11 }} tickFormatter={(v) => {
                          const d = new Date(v);
                          return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
                        }}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#2d6a4f" strokeWidth={2.5} dot={false} />
                        {compare && (
                          <Line type="monotone" dataKey="compare" name="Prior period" stroke="#b08968" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </ChartPanel>

                <ChartPanel
                  title="Events Over Time"
                  summary="Platform eventAnalytics by type (top types stacked as series)"
                  empty={!eventSeries.some((d) => (d.totalEvents || 0) > 0)}
                  emptyTitle="No analytics events"
                  emptyHint="Event tracking will fill this once clients emit events."
                  onExport={() => downloadCsv('events-over-time.csv', eventSeries)}
                >
                  <div className="adm-an-chart__body" role="img" aria-label="Bar chart of analytics events over time">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={eventSeries}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,68,42,0.12)" />
                        <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        {eventTypes.length ? eventTypes.map((t, i) => (
                          <Bar key={t} dataKey={t} stackId="a" fill={EVENT_COLORS[i % EVENT_COLORS.length]} />
                        )) : (
                          <Bar dataKey="totalEvents" name="Events" fill="#18442A" />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartPanel>

                <ChartPanel
                  title="Conversion Funnel"
                  summary="User journey stages from behavior tracking"
                  empty={funnelChartData.length === 0}
                  emptyTitle="No funnel data"
                  emptyHint="Funnel stages appear when page/session tracking is active."
                  onExport={() => downloadCsv('conversion-funnel.csv', funnelChartData)}
                >
                  <div className="adm-an-chart__body" role="img" aria-label="Conversion funnel bar chart">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={funnelChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,68,42,0.12)" />
                        <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" name="Users" radius={[6, 6, 0, 0]}>
                          {funnelChartData.map((_, index) => (
                            <Cell key={`f-${index}`} fill={FUNNEL_COLORS[index % FUNNEL_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartPanel>

                <ChartPanel
                  title="Top Searches"
                  summary="Click a bar label row below to open Activity"
                  empty={searchChartData.length === 0}
                  emptyTitle="No search terms"
                  emptyHint="Search analytics will show once users search."
                  onExport={() => downloadCsv('top-searches.csv', searchChartData)}
                >
                  <div className="adm-an-chart__body" role="img" aria-label="Top searches bar chart">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={searchChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,68,42,0.12)" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="term" width={110} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" name="Searches" fill="#18442A" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="adm-an-drill-list">
                    {searchChartData.map((s) => (
                      <li key={s.term}>
                        <button type="button" onClick={() => drillSearch(s.term)}>
                          Drill: {s.term} ({s.count})
                        </button>
                      </li>
                    ))}
                  </ul>
                </ChartPanel>
              </div>
            </>
          )}

          {activeTab === 'behavior' && (
            <div className="adm-an-tab">
              <header className="adm-an-section__head">
                <h3>User Behavior</h3>
                <p>Engagement metrics and journey surfaces for the selected range.</p>
              </header>
              {behaviorError && (
                <div className="adm-error" role="alert">
                  <span>{behaviorError}</span>
                  <button type="button" className="btn-sm btn-secondary" onClick={() => setBehaviorTick((n) => n + 1)}>Retry</button>
                </div>
              )}
              {behaviorLoading ? <SkeletonGrid count={4} /> : (
                <div className="adm-an-grid adm-an-grid--4">
                  <MetricCard label="Live Users" value={liveActiveUsers} hint={`Window ${liveWindowLabel}`} tone="primary" />
                  <MetricCard label="Page Views" value={formatNumber(pageViewsTotal)} hint="Total views this period" />
                  <MetricCard
                    label="Sessions"
                    value={formatNumber(engagement?.totalSessions ?? deviceSessionsTotal)}
                    hint="Tracked sessions"
                    tone="info"
                  />
                  <MetricCard
                    label="Avg. Session"
                    value={engagement?.avgSessionDuration != null
                      ? `${Math.round(engagement.avgSessionDuration)}s`
                      : (devices.length
                        ? `${Math.round(devices.reduce((a, d) => a + (d.avgSessionTime || 0), 0) / devices.length)}s`
                        : '0s')}
                    hint={engagement?.bounceRate != null ? `Bounce ${Math.round(engagement.bounceRate * 100)}%` : 'Session duration'}
                    tone="accent"
                  />
                </div>
              )}

              <div className="adm-an-charts">
                <ChartPanel title="Popular Pages" summary="Pages with the most views" empty={!pageViews.length} emptyTitle="No page views">
                  <div className="adm-an-list adm-an-list--pages">
                    {pageViews.map((page, index) => {
                      const raw = page.page || page._id || 'Unknown';
                      const name = String(raw).replace(/^\/api\//, '/');
                      const views = (page.viewCount ?? page.count) || 0;
                      return (
                        <div key={`${name}-${index}`} className="adm-an-list__row">
                          <div className="adm-an-list__main">
                            <strong className="adm-an-list__path">{name}</strong>
                            <div className="adm-an-list__meta">
                              {page.avgDuration != null && <span>Avg {Math.round(page.avgDuration)}s</span>}
                              {page.avgScrollDepth != null && <span>Scroll {Math.round(page.avgScrollDepth)}%</span>}
                              {page.exitRate != null && <span>Exit {Math.round(page.exitRate * 100)}%</span>}
                            </div>
                          </div>
                          <span className="adm-an-list__kpi">{views} views</span>
                        </div>
                      );
                    })}
                  </div>
                </ChartPanel>

                <ChartPanel title="Device Mix" summary="Sessions by device / browser" empty={!devices.length} emptyTitle="No device data">
                  <div className="adm-an-list">
                    {devices.map((d, index) => {
                      const type = d.deviceType || String(d._id || '').split(' - ')[0] || 'Unknown';
                      const sessions = d.sessionCount ?? d.count ?? 0;
                      return (
                        <div key={`${type}-${index}`} className="adm-an-list__row">
                          <div>
                            <strong>{type}</strong>
                            <div className="adm-an-list__meta">
                              {d.browser && <span>{d.browser}</span>}
                              {d.os && <span>{d.os}</span>}
                              {d.uniqueUserCount != null && <span>{d.uniqueUserCount} unique</span>}
                            </div>
                          </div>
                          <span className="adm-an-list__kpi">{sessions} sessions</span>
                        </div>
                      );
                    })}
                  </div>
                </ChartPanel>
              </div>
            </div>
          )}

          {activeTab === 'business' && (
            <div className="adm-an-tab">
              <header className="adm-an-section__head">
                <h3>Business Performance</h3>
                <p>Only partner-scoped metrics from /analytics/business — no platform bleed.</p>
              </header>
              {businessError && (
                <div className="adm-error" role="alert">
                  <span>{businessError}</span>
                  <button type="button" className="btn-sm btn-secondary" onClick={() => setBusinessTick((n) => n + 1)}>Retry</button>
                </div>
              )}
              {businessLoading ? <SkeletonGrid count={4} /> : (
                <div className="adm-an-grid adm-an-grid--4">
                  <MetricCard
                    label="Bookings"
                    value={formatNumber(biz?.totalBookings || 0)}
                    hint={biz?.completedBookings != null ? `${formatNumber(biz.completedBookings)} completed` : 'In range'}
                    tone="primary"
                  />
                  <MetricCard
                    label="Revenue"
                    value={formatCurrency(biz?.totalRevenue || 0)}
                    hint={biz?.avgBookingValue != null ? `Avg ${formatCurrency(biz.avgBookingValue)}` : 'Completed'}
                    tone="success"
                  />
                  <MetricCard
                    label="Cancellation Rate"
                    value={biz?.cancellationRate != null ? `${(Number(biz.cancellationRate) * 100).toFixed(1)}%` : '—'}
                    hint="Share of cancelled bookings"
                    tone="warning"
                  />
                  <MetricCard
                    label="Avg Rating"
                    value={businessData?.reviewAnalytics?.avgRating != null
                      ? Number(businessData.reviewAnalytics.avgRating).toFixed(1)
                      : '—'}
                    hint={businessData?.reviewAnalytics?.totalReviews != null
                      ? `${formatNumber(businessData.reviewAnalytics.totalReviews)} reviews`
                      : 'Review mix'}
                    tone="info"
                  />
                </div>
              )}

              <div className="adm-an-charts">
                <ChartPanel
                  title="By Category"
                  summary="Bookings and revenue by service category"
                  empty={!categoryChart.length}
                  emptyTitle="No category mix"
                  onExport={() => downloadCsv('business-categories.csv', categoryChart)}
                >
                  <div className="adm-an-chart__body" role="img" aria-label="Category bookings chart">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={categoryChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,68,42,0.12)" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="bookings" name="Bookings" fill="#18442A" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartPanel>

                <ChartPanel
                  title="By Location"
                  summary="Bookings by town"
                  empty={!locationChart.length}
                  emptyTitle="No location mix"
                  onExport={() => downloadCsv('business-locations.csv', locationChart)}
                >
                  <div className="adm-an-chart__body" role="img" aria-label="Location bookings chart">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={locationChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,68,42,0.12)" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="bookings" name="Bookings" fill="#2d6a4f" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartPanel>

                <ChartPanel
                  title="Top Businesses"
                  summary="Drill into Providers with the business name prefilled"
                  empty={!businessData?.topBusinesses?.length}
                  emptyTitle="No top businesses"
                >
                  <div className="adm-an-list">
                    {(businessData?.topBusinesses || []).map((b, index) => (
                      <div key={b.businessId || index} className="adm-an-list__row">
                        <div>
                          <strong>{b.businessName || 'Unknown'}</strong>
                          <div className="adm-an-list__meta">
                            {b.serviceCategory && <span>{b.serviceCategory}</span>}
                            {b.town && <span>{b.town}</span>}
                          </div>
                        </div>
                        <div className="adm-an-list__actions">
                          <span className="adm-an-list__kpi">{b.bookingCount || 0} · {formatCurrency(b.revenue || 0)}</span>
                          <button type="button" className="adm-an-link" onClick={() => drillBusiness(b)}>Open</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ChartPanel>
              </div>
            </div>
          )}

          {activeTab === 'realtime' && (
            <div className="adm-an-tab">
              <header className="adm-an-section__head adm-an-section__head--row">
                <div>
                  <h3>Live Monitoring</h3>
                  <p>
                    Polls every 15s · window {liveWindowLabel}
                    {realtimeUpdatedAt ? ` · Updated ${realtimeUpdatedAt.toLocaleTimeString()}` : ''}
                  </p>
                </div>
                <button type="button" className="btn-sm btn-secondary" onClick={loadRealtimeOnly}>Refresh now</button>
              </header>

              <div className="adm-an-grid adm-an-grid--4">
                <MetricCard label="Active Users" value={liveActiveUsers} hint={`Last ${liveWindowLabel}`} tone="primary" />
                <MetricCard label="Recent Bookings" value={realTimeData?.recentBookings || 0} hint={`Last ${liveWindowLabel}`} tone="success" />
                <MetricCard label="System Events" value={realTimeData?.recentEvents || 0} hint={`Last ${liveWindowLabel}`} />
                <MetricCard
                  label="Database"
                  value={realTimeData?.systemHealth?.databaseStatus || 'Unknown'}
                  hint={realTimeData?.systemHealth?.uptime ? `Uptime ${realTimeData.systemHealth.uptime}` : 'Status'}
                  tone="info"
                />
              </div>

              <ChartPanel title="Live Activity Feed" summary="Recent platform activity" empty={!realTimeData?.recentActivity?.length} emptyTitle="No recent activity" emptyHint="Activity in the last hour will show here.">
                <div className="adm-an-feed">
                  {(realTimeData?.recentActivity || []).map((activity, index) => (
                    <div key={index} className="adm-an-feed__item">
                      <span className="adm-an-feed__icon" aria-hidden="true">
                        {activity.type === 'booking' && <FaClipboardList />}
                        {activity.type === 'user' && <FaUsers />}
                        {activity.type === 'system' && <FaCog />}
                        {!['booking', 'user', 'system'].includes(activity.type) && <FaBell />}
                      </span>
                      <div>
                        <div>{activity.message || activity.description || activity.type}</div>
                        <time>{activity.timestamp || ''}</time>
                      </div>
                    </div>
                  ))}
                </div>
              </ChartPanel>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="adm-an-tab">
              <header className="adm-an-section__head">
                <h3>Reports</h3>
                <p>Insights and recommendations come from the reporting APIs — no cosmetic feature tags.</p>
              </header>

              <div className="adm-an-reports">
                <article className="adm-an-report-card">
                  <FaChartBar aria-hidden="true" />
                  <h4>KPI Dashboard</h4>
                  <p>Key performance indicators for the selected range.</p>
                  <button type="button" className="submit-button" onClick={() => loadReport('kpi')} disabled={reportsLoading}>
                    View KPIs
                  </button>
                </article>
                <article className="adm-an-report-card">
                  <FaChartLine aria-hidden="true" />
                  <h4>Trend Analysis</h4>
                  <p>Direction and strength for bookings over time.</p>
                  <button type="button" className="submit-button" onClick={() => loadReport('trends')} disabled={reportsLoading}>
                    Analyze Trends
                  </button>
                </article>
                <article className="adm-an-report-card">
                  <FaMagic aria-hidden="true" />
                  <h4>Predictive Analytics</h4>
                  <p>Forecast demand and seasonal recommendations from the API.</p>
                  <button type="button" className="submit-button" onClick={() => loadReport('predictions')} disabled={reportsLoading}>
                    Run Forecast
                  </button>
                </article>
                <article className="adm-an-report-card">
                  <FaFileExport aria-hidden="true" />
                  <h4>Export CSV</h4>
                  <p>Download the comprehensive report for this range.</p>
                  <button type="button" className="btn-secondary" onClick={exportReports}>
                    Export CSV
                  </button>
                </article>
              </div>

              {reportsError && (
                <div className="adm-error" role="alert">
                  <span>{reportsError}</span>
                  <button type="button" className="btn-sm btn-secondary" onClick={() => loadReport(reportKind)}>Retry</button>
                </div>
              )}

              {reportsLoading && (
                <div className="adm-an-loading" aria-busy="true">
                  <SkeletonGrid count={2} />
                </div>
              )}

              {!reportsLoading && !reportsError && !reportsData && (
                <ChartEmpty title="No report loaded" hint="Choose KPI, Trends, or Forecast above." />
              )}

              {reportsData && !reportsLoading && (
                <div className="adm-an-report-result">
                  <h4>{reportsData.title}</h4>
                  <div className="adm-an-insights">
                    {(reportsData.insights || []).map((insight, i) => (
                      <article key={i} className="adm-an-insight">
                        <FaLightbulb aria-hidden="true" />
                        <div><p>{insight}</p></div>
                      </article>
                    ))}
                  </div>
                  <h4>Recommendations</h4>
                  {(reportsData.recommendations || []).length ? (
                    <ul className="adm-an-recs">
                      {reportsData.recommendations.map((rec, i) => (
                        <li key={i}>
                          <FaCheckCircle aria-hidden="true" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ChartEmpty title="No API recommendations" hint="This report type returned insights only." />
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
